import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, BookCheck, Wrench, FileText, Users, Award, ChevronRight, Star, Building2, Bed, Compass, Search, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import PlanCard from "@/components/PlanCard";
import LeadCaptureForm from "@/components/LeadCaptureForm";
import PlotCompass, { emptySides, sidesToUrlParams } from "@/components/PlotCompass";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEO from "@/components/SEO";

const PLOT_SURROUNDINGS_KEY = "nc:plotSurroundings";

const FACINGS = [
  { v: "N", labelKey: "facing.N" },
  { v: "S", labelKey: "facing.S" },
  { v: "E", labelKey: "facing.E" },
  { v: "W", labelKey: "facing.W" },
  { v: "NE", labelKey: "facing.NE" },
  { v: "NW", labelKey: "facing.NW" },
  { v: "SE", labelKey: "facing.SE" },
  { v: "SW", labelKey: "facing.SW" },
];

const CATEGORIES = [
  { name: "30x40 Plans", q: "?min_size=1100&max_size=1300", img: "https://images.pexels.com/photos/4458210/pexels-photo-4458210.jpeg" },
  { name: "North-Facing", q: "?facing=N", img: "https://images.unsplash.com/photo-1721244654195-943615c56ac4" },
  { name: "Duplex Homes", q: "?category=duplex", img: "https://images.pexels.com/photos/20445164/pexels-photo-20445164.jpeg" },
  { name: "Vastu Compliant", q: "?category=vastu", img: "https://images.pexels.com/photos/5582866/pexels-photo-5582866.jpeg" },
  { name: "Budget Homes", q: "?category=budget", img: "https://images.unsplash.com/photo-1503387762-592deb58ef4e" },
  { name: "Luxury Villas", q: "?category=luxury", img: "https://images.pexels.com/photos/37129015/pexels-photo-37129015.jpeg" },
];

const STATES = ["Maharashtra", "Bihar", "Uttar Pradesh", "Karnataka", "Telangana", "Tamil Nadu", "Gujarat", "Rajasthan", "West Bengal", "Madhya Pradesh", "Punjab", "Haryana"];

const FAQ_KEYS = [
  ["homeExtra.faq1Q", "homeExtra.faq1A"],
  ["homeExtra.faq2Q", "homeExtra.faq2A"],
  ["homeExtra.faq3Q", "homeExtra.faq3A"],
  ["homeExtra.faq4Q", "homeExtra.faq4A"],
];

const PARTNER_TAGS = ["homeExtra.partnerTag1", "homeExtra.partnerTag2", "homeExtra.partnerTag3", "homeExtra.partnerTag4", "homeExtra.partnerTag5", "homeExtra.partnerTag6", "homeExtra.partnerTag7", "homeExtra.partnerTag8"];

const TESTIMONIALS = [
  { nameKey: "homeExtra.tm1Name", cityKey: "homeExtra.tm1City", quoteKey: "homeExtra.tm1Q", img: "https://images.pexels.com/photos/9346163/pexels-photo-9346163.jpeg" },
  { nameKey: "homeExtra.tm2Name", cityKey: "homeExtra.tm2City", quoteKey: "homeExtra.tm2Q", img: "https://images.unsplash.com/photo-1503387762-592deb58ef4e" },
  { nameKey: "homeExtra.tm3Name", cityKey: "homeExtra.tm3City", quoteKey: "homeExtra.tm3Q", img: "https://images.pexels.com/photos/5582866/pexels-photo-5582866.jpeg" },
];

export default function Home() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState({ plans: 1200, partners: 350, customers: 8500 });
  const [offerBanner, setOfferBanner] = useState(null);
  const [heroImages, setHeroImages] = useState(["/hero-home.jpg"]);
  const [heroIdx, setHeroIdx] = useState(0);
  const [search, setSearch] = useState({ length: "", width: "", floors: 1, facing: "N" });
  const [sides, setSides] = useState(emptySides());
  const [showSides, setShowSides] = useState(false);

  useEffect(() => {
    api.get("/plans?featured=true&limit=8").then(({ data }) => setPlans(data)).catch(() => {});
    api.get("/home-content").then(({ data }) => {
      if (data?.stats) setStats(data.stats);
      if (data?.offer_banner?.enabled && data.offer_banner.text) setOfferBanner(data.offer_banner);
      if (Array.isArray(data?.hero_images) && data.hero_images.length) setHeroImages(data.hero_images);
    }).catch(() => {});
    try {
      const cached = sessionStorage.getItem(PLOT_SURROUNDINGS_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        setSides({ ...emptySides(), ...parsed });
        if (Object.values(parsed).some(Boolean)) setShowSides(true);
      }
    } catch {}
  }, []);

  const sidesFilled = Object.values(sides).filter(Boolean).length;

  useEffect(() => {
    if (heroImages.length < 2) return undefined;
    const id = setInterval(() => setHeroIdx(i => (i + 1) % heroImages.length), 3000);
    return () => clearInterval(id);
  }, [heroImages]);

  const submitSearch = (e) => {
    e.preventDefault();
    try { sessionStorage.setItem(PLOT_SURROUNDINGS_KEY, JSON.stringify(sides)); } catch {}
    const q = new URLSearchParams({
      length: String(search.length || ""),
      width: String(search.width || ""),
      floors: String(search.floors),
      facing: search.facing,
      ...sidesToUrlParams(sides),
    });
    nav(`/plans?${q.toString()}`);
  };

  return (
    <div data-testid="home-page">
      <SEO
        title="Get Your House Plan, BOQ & Cost Estimate in 2 Minutes"
        description="Search by plot size, floors and facing to get matched house plans instantly. Verified architects & contractors across India."
        path="/"
      />
      {offerBanner && (
        <a
          href={offerBanner.link || "#"}
          className="block bg-emerald-600 text-white overflow-hidden border-b border-emerald-700"
          data-testid="home-offer-banner"
        >
          <div className="nc-marquee whitespace-nowrap py-2 text-sm font-medium">
            <span className="px-8">{offerBanner.text}</span>
            <span className="px-8">{offerBanner.text}</span>
            <span className="px-8">{offerBanner.text}</span>
          </div>
        </a>
      )}
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-emerald-50/40 to-white">
        <div className="absolute -top-32 -left-24 w-96 h-96 rounded-full bg-emerald-300 nc-blob" />
        <div className="absolute -top-12 right-0 w-[500px] h-[500px] rounded-full bg-sky-300 nc-blob" style={{ animationDelay: "3s" }} />
        <div className="relative nc-container pt-14 pb-16 md:pt-20 md:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="nc-chip" data-testid="hero-tagline">
                <Sparkles className="w-3.5 h-3.5" /> {t("hero.tagline")}
              </span>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                {t("hero.h1a")} <span className="nc-gradient-text">{t("hero.h1b")}</span>
              </h1>
              <p className="mt-5 text-lg text-slate-600 max-w-xl leading-relaxed">{t("hero.sub")}</p>

              {/* Plot search */}
              <form onSubmit={submitSearch} className="mt-7 bg-white rounded-2xl border border-slate-200 nc-soft-shadow p-3 md:p-4" data-testid="hero-search-form">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3">
                  <input className="nc-input" placeholder={t("hero.length")} type="number" value={search.length} onChange={(e) => setSearch({ ...search, length: e.target.value })} required data-testid="hero-length-input" />
                  <input className="nc-input" placeholder={t("hero.width")} type="number" value={search.width} onChange={(e) => setSearch({ ...search, width: e.target.value })} required data-testid="hero-width-input" />
                  <select className="nc-input" value={search.floors} onChange={(e) => setSearch({ ...search, floors: parseInt(e.target.value) })} data-testid="hero-floors-select">
                    {[1, 2, 3, 4].map((f) => <option key={f} value={f}>{f} {t("plan.floors")}</option>)}
                  </select>
                  <div className="relative">
                    <label className="absolute -top-2 left-3 text-[10px] font-medium text-slate-400 bg-white px-1 leading-none z-10">{t("hero.facingLabel")}</label>
                    <select className="nc-input" value={search.facing} onChange={(e) => setSearch({ ...search, facing: e.target.value })} data-testid="hero-facing-select">
                      {FACINGS.map(f => <option key={f.v} value={f.v}>{t(f.labelKey)}</option>)}
                      <option value="ANY">{t("facing.ANY")}</option>
                    </select>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSides((s) => !s)}
                  className="mt-3 w-full flex items-center justify-between text-left text-xs font-semibold text-slate-700 hover:text-emerald-700 px-2 py-1.5 rounded-lg hover:bg-emerald-50 transition"
                  data-testid="hero-sides-toggle"
                  aria-expanded={showSides}
                >
                  <span className="flex items-center gap-1.5">
                    <Compass className="w-3.5 h-3.5 text-emerald-600" />
                    {t("hero.surroundingsToggle")} <span className="text-slate-400 font-normal">{t("hero.surroundingsOptional")}</span>
                    {sidesFilled > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px]">{sidesFilled}/4</span>
                    )}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showSides ? "rotate-180" : ""}`} />
                </button>
                {showSides && (
                  <div className="mt-3 px-2 pb-1" data-testid="hero-sides-panel">
                    <PlotCompass value={sides} onChange={setSides} testidPrefix="hero-compass" />
                    <p className="mt-2 text-[11px] text-slate-500 text-center">
                      {t("hero.surroundingsHelp")}
                    </p>
                  </div>
                )}

                <button type="submit" className="mt-3 nc-btn-primary w-full justify-center" data-testid="hero-search-submit">
                  <Search className="w-4 h-4" /> {t("hero.findPlan")} <ArrowRight className="w-4 h-4" />
                </button>
                <p className="mt-3 text-xs text-emerald-700 flex items-center gap-1.5">
                  <Compass className="w-3.5 h-3.5" /> {t("hero.vastuToggle")}
                </p>
              </form>

              <div className="mt-6 flex flex-wrap gap-2 text-xs text-slate-500">
                {[t("hero.chip1"), t("hero.chip2"), t("hero.chip3")].map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-white border border-slate-200">{tag}</span>
                ))}
              </div>
            </div>

            <div className="hidden lg:block lg:col-span-5">
              <div className="relative">
                <img
                  key={heroIdx}
                  src={heroImages[heroIdx] || "/hero-home.jpg"}
                  alt="Modern Indian home"
                  className="rounded-3xl shadow-2xl object-cover w-full aspect-[4/5] nc-hero-fade"
                  data-testid="hero-image"
                />
                <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl p-4 shadow-xl border border-slate-100 max-w-[260px]">
                  <div className="flex items-center gap-2 mb-1">
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  </div>
                  <p className="text-sm text-slate-700 leading-snug">{t("homeExtra.featuredQuote")}</p>
                  <p className="text-xs text-slate-500 mt-1">{t("homeExtra.featuredQuoteAuthor")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-slate-100 bg-white">
        <div className="nc-container py-8 flex flex-wrap items-center justify-around gap-6 text-sm text-slate-400 font-medium">
          <span className="text-xs uppercase tracking-[0.25em] text-slate-500">{t("sections.asFeaturedIn")}</span>
          {["The Hindu", "ET Realty", "YourStory", "Inc42", "Hindustan Times"].map((b) => (
            <span key={b} className="text-base font-semibold text-slate-400 hover:text-slate-700 transition" style={{ fontFamily: "Poppins" }}>{b}</span>
          ))}
        </div>
      </section>

      {/* Featured plans */}
      <section className="nc-section">
        <div className="nc-container">
          <SectionHeader title={t("sections.featured")} sub={t("sections.featuredSub")} action={{ label: t("sections.featuredAction"), to: "/plans" }} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
            {plans.slice(0, 4).map(p => <PlanCard key={p.id} plan={p} />)}
            {plans.length === 0 && Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="nc-card animate-pulse">
                <div className="aspect-[4/3] bg-slate-100" />
                <div className="p-4 space-y-2">
                  <div className="h-3 bg-slate-100 rounded" />
                  <div className="h-3 bg-slate-100 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="nc-section pt-0">
        <div className="nc-container">
          <SectionHeader title={t("sections.categories")} sub={t("sections.categoriesSub")} />
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {CATEGORIES.map((c) => (
              <a key={c.name} href={`/plans${c.q}`} className="group relative rounded-2xl overflow-hidden border border-slate-200 hover:border-emerald-400 transition" data-testid={`category-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="aspect-square bg-slate-100">
                  <img src={c.img} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <div className="text-white font-semibold text-sm" style={{ fontFamily: "Poppins" }}>{c.name}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="nc-section bg-gradient-to-b from-white to-emerald-50/30">
        <div className="nc-container">
          <SectionHeader title={t("sections.how")} sub={t("sections.howSub")} centered />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: Search, key: "s1", color: "bg-emerald-100 text-emerald-700" },
              { icon: BookCheck, key: "s2", color: "bg-sky-100 text-sky-700" },
              { icon: FileText, key: "s3", color: "bg-emerald-100 text-emerald-700" },
              { icon: Users, key: "s4", color: "bg-sky-100 text-sky-700" },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6 nc-soft-shadow">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.color} mb-4`}>
                  <s.icon className="w-6 h-6" />
                </div>
                <div className="text-xs font-semibold text-slate-400 mb-1">{t("how.step")} {i + 1}</div>
                <h3 className="font-semibold text-slate-900 mb-1.5" style={{ fontFamily: "Poppins" }}>{t(`how.${s.key}`)}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{t(`how.${s.key}d`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-slate-900 text-white">
        <div className="nc-container py-14">
          <div className="text-center mb-8">
            <span className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-semibold">{t("sections.stats")}</span>
          </div>
          <div className="grid grid-cols-3 gap-6 text-center">
            <Stat value={stats.plans} suffix="+" label={t("homeExtra.stat1")} />
            <Stat value={stats.partners} suffix="+" label={t("homeExtra.stat2")} />
            <Stat value={stats.customers} suffix="+" label={t("homeExtra.stat3")} />
          </div>
        </div>
      </section>

      {/* Lead capture block */}
      <section className="nc-section">
        <div className="nc-container grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">{t("sections.leadTitle")}</h2>
            <p className="mt-4 text-slate-600 text-lg leading-relaxed">{t("sections.leadSub")}</p>
            <ul className="mt-6 space-y-2.5 text-sm text-slate-700">
              <li className="flex items-start gap-2"><Award className="w-5 h-5 text-emerald-600 flex-shrink-0" /> {t("homeExtra.benefit1")}</li>
              <li className="flex items-start gap-2"><Award className="w-5 h-5 text-emerald-600 flex-shrink-0" /> {t("homeExtra.benefit2")}</li>
              <li className="flex items-start gap-2"><Award className="w-5 h-5 text-emerald-600 flex-shrink-0" /> {t("homeExtra.benefit3")}</li>
            </ul>
          </div>
          <div className="lg:col-span-7">
            <LeadCaptureForm source="home_form" dataTestidPrefix="home-lead" />
          </div>
        </div>
      </section>

      {/* State-wise */}
      <section className="nc-section pt-0">
        <div className="nc-container">
          <SectionHeader title={t("sections.states")} />
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {STATES.map((s) => (
              <a key={s} href={`/plans?state=${encodeURIComponent(s)}`} className="px-5 py-4 rounded-xl bg-white border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 transition flex items-center justify-between group" data-testid={`state-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="font-medium text-slate-700 group-hover:text-emerald-700">{t("homeExtra.stateLink", { state: s })}</span>
                <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-700" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="nc-section pt-0">
        <div className="nc-container">
          <SectionHeader title={t("sections.whyUs")} sub={t("sections.whyUsSub")} centered />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Sparkles, k: "one" },
              { icon: Users, k: "two" },
              { icon: FileText, k: "three" },
            ].map((it, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-emerald-300 transition">
                <div className="w-12 h-12 rounded-xl nc-gradient flex items-center justify-center text-white mb-4">
                  <it.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1.5" style={{ fontFamily: "Poppins" }}>{t(`why.${it.k}`)}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{t(`why.${it.k}D`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="nc-section pt-0">
        <div className="nc-container">
          <SectionHeader title={t("sections.testimonials")} centered />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((tm, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <img src={tm.img} alt="" className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <div className="font-semibold text-slate-900 text-sm">{t(tm.nameKey)}</div>
                    <div className="text-xs text-slate-500">{t(tm.cityKey)}</div>
                  </div>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">"{t(tm.quoteKey)}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner showcase */}
      <section className="nc-section pt-0">
        <div className="nc-container">
          <div className="bg-gradient-to-br from-emerald-50 to-sky-50 rounded-3xl p-8 md:p-12 border border-emerald-100 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="flex flex-wrap gap-2">
                {PARTNER_TAGS.map((k) => (
                  <span key={k} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-medium text-slate-700">{t(k)}</span>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href="/customize" className="nc-btn-primary" data-testid="partner-cta-consult">{t("homeExtra.getQuote")}</a>
                <a href="/partner/register" className="nc-btn-secondary" data-testid="partner-cta-register">{t("homeExtra.registerPartner")}</a>
              </div>
            </div>
            <div className="lg:col-span-5 grid grid-cols-3 gap-2">
              {["https://images.unsplash.com/photo-1503387762-592deb58ef4e", "https://images.pexels.com/photos/5582866/pexels-photo-5582866.jpeg", "https://images.pexels.com/photos/4458210/pexels-photo-4458210.jpeg", "https://images.pexels.com/photos/20445164/pexels-photo-20445164.jpeg", "https://images.pexels.com/photos/37129015/pexels-photo-37129015.jpeg", "https://images.unsplash.com/photo-1721244654195-943615c56ac4"].map((src, i) => (
                <img key={i} src={src} alt="" className="aspect-square rounded-xl object-cover" loading="lazy" />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="nc-section pt-0">
        <div className="nc-container max-w-3xl">
          <SectionHeader title={t("sections.faq")} centered />
          <div className="mt-8">
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {FAQ_KEYS.map(([qK, aK], i) => (
                <AccordionItem key={i} value={`q-${i}`} className="border border-slate-200 rounded-xl px-4">
                  <AccordionTrigger className="text-left font-medium text-slate-900" data-testid={`faq-trigger-${i}`}>{t(qK)}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">
                    {String(t(aK)).split("\n\n").map((para, j) => <p key={j} className={j > 0 ? "mt-3" : ""}>{para}</p>)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ title, sub, action, centered }) {
  return (
    <div className={`flex flex-col sm:flex-row sm:items-end gap-3 ${centered ? "items-center text-center sm:items-center sm:justify-center" : "sm:justify-between"}`}>
      <div className={centered ? "text-center" : ""}>
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">{title}</h2>
        {sub && <p className="mt-2 text-slate-600 max-w-2xl">{sub}</p>}
      </div>
      {action && (
        <a href={action.to} className="text-sm font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1">
          {action.label} <ArrowRight className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}

function Stat({ value, suffix, label }) {
  return (
    <div>
      <div className="text-4xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: "Poppins" }}>
        {value.toLocaleString("en-IN")}{suffix}
      </div>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mt-2">{label}</div>
    </div>
  );
}
